import { useStore } from '@nanostores/react';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { EnterpriseCard } from './Card/EnterpriseCard';
import { FreePlanCard } from './Card/FreePlanCard';
import { ProPlansCard } from './Card/ProPlansCard';
import { TeamPlansCard } from './Card/TeamPlansCard';
import { RefreshSubscriptionButton } from './RefreshSubscriptionButton';
import { LoginDialog } from '~/components/chat/LoginDialog.client';
import { WINDOW_RELOAD_TIMEOUT } from '~/lib/constants';
import { useTokenStats } from '~/lib/hooks/useTokenStats';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { proPricingPlansStore } from '~/lib/stores/pricing/proPlans';
import { teamPricingPlansStore } from '~/lib/stores/pricing/teamPlans';
import type { PricingWorkspace } from '~/lib/stores/pricing/types';
import { workspacesStore } from '~/lib/stores/pricing/workspaces';
import { userStore } from '~/lib/stores/user';
import { SELECTED_ACCOUNT_USER } from '~/utils/constants';
import { cubicEasingFn } from '~/utils/easings';
import { formatTokens } from '~/utils/format';
import { computeTokensLeft } from '~/utils/tokens-stats';

export const PricingCards = () => {
  const user = useStore(userStore.user);
  const loggedIn = useStore(userStore.loggedIn);
  const authDialogOpen = useStore(pricingStore.authDialogOpen);
  const workspace = useStore(workspacesStore.selectedWorkspace);
  const tier = useStore(userStore.getTierStore());

  const [tokenStatsMessage, setTokenStatsMessage] = useState<string | null>(null);

  const { tokenStats, fetchTokenStats } = useTokenStats();

  function fetchTokenForWorkspace(workspace: PricingWorkspace) {
    if (workspace.type === 'team') {
      fetchTokenStats(workspace.org);
      return;
    }

    if (workspace.type === 'personal') {
      fetchTokenStats(SELECTED_ACCOUNT_USER);
      return;
    }
  }
  useEffect(() => {
    if (!loggedIn) {
      proPricingPlansStore.initUnauthorizedState();
      teamPricingPlansStore.initUnauthorizedState();
    }
  }, [loggedIn]);

  useEffect(() => {
    fetchTokenForWorkspace(workspace);
  }, [workspace]);

  useEffect(() => {
    if (tokenStats) {
      const remainingTokens = formatTokens(computeTokensLeft(tokenStats, tier, 'monthly').remainingTokens);

      setTokenStatsMessage(`You have ${remainingTokens} tokens left`);
    } else {
      setTokenStatsMessage(null);
    }
  }, [tokenStats]);

  function onLoginSuccess() {
    pricingStore.authDialogOpen.set(false);

    setTimeout(() => {
      window.location.reload();
    }, WINDOW_RELOAD_TIMEOUT);
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.15, ease: cubicEasingFn }}
        className="flex flex-col gap-4 md:grid md:grid-cols-2 xl:grid-cols-4"
      >
        <FreePlanCard />

        <ProPlansCard
          isPersonalWorkspace={workspace.type === 'personal'}
          tokenStatsMessage={workspace.type === 'personal' ? tokenStatsMessage : null}
        />

        <TeamPlansCard tokenStatsMessage={workspace.type === 'team' ? tokenStatsMessage : null} />

        <EnterpriseCard />
      </motion.div>

      {user?.featureFlags?.refreshSubscriptionData && (workspace.type === 'team' || workspace.type === 'personal') && (
        <RefreshSubscriptionButton
          plan={workspace.type === 'team' ? 'teams' : 'pro'}
          org={workspace.type === 'team' ? workspace.org : null}
        />
      )}

      {!loggedIn && (
        <LoginDialog
          open={authDialogOpen}
          onClose={() => pricingStore.authDialogOpen.set(false)}
          onLoginSuccess={onLoginSuccess}
        />
      )}
    </>
  );
};
